import { customersControllerFindAll } from '@kalba/api-client';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import {
  AlertCircle,
  Building2,
  Edit2,
  FileCheck,
  MoreHorizontal,
  Plus,
  Search,
  SlidersHorizontal,
  UserCheck,
  UserRound,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { AppLayout } from '../../components/layout/app-layout';
import { PageHeader } from '../../components/layout/page-header';
import {
  CardDataTable,
  type CardDataTableColumn,
  type CardDataTableRowDef,
} from '../../components/shared/card-data-table';
import { GradientAvatar } from '../../components/shared/gradient-avatar';
import { StatCard, StatCardSkeleton } from '../../components/shared/stat-card';
import { Button } from '../../components/ui/button';
import { FilterPills } from '../../components/ui/filter-pills';
import { usePermissions } from '../../hooks/use-permissions';
import { api } from '../../lib/ky';
import { cn } from '../../lib/utils';
import { useAuthStore } from '../../stores/auth.store';

interface CustomerListItem {
  id: string;
  name: string;
  accountType: string;
  status: string;
  _count: { contacts: number; permits: number };
}

interface PaginatedResponse {
  data: CustomerListItem[];
  total: number;
  page: number;
  limit: number;
}

export const Route = createFileRoute('/customers/')({
  beforeLoad: () => {
    const { user, permissions } = useAuthStore.getState();
    if (!user) throw redirect({ to: '/login' });
    if (!permissions.includes('customer:view')) throw redirect({ to: '/dashboard' });
  },
  component: CustomersPage,
});

const CUSTOMER_FILTER_PILLS = [
  { value: '__all__', label: 'All' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

function CustomerStatusBadge({ status }: { status: string }) {
  const isActive = status === 'ACTIVE';
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold',
        isActive
          ? 'border-success-200 bg-success-50 text-success-600'
          : 'border-gray-200 bg-gray-50 text-gray-500',
      )}
    >
      <span
        className={cn('h-1.5 w-1.5 rounded-full', isActive ? 'bg-success-500' : 'bg-gray-400')}
      />
      {isActive ? 'Active' : status.charAt(0) + status.slice(1).toLowerCase()}
    </span>
  );
}

// ── List table columns ───────────────────────────────────────────────────────

const CUSTOMER_COLUMNS: CardDataTableColumn[] = [
  { id: 'account', header: 'Account' },
  { id: 'status', header: 'Status' },
  { id: 'contacts', header: 'Contacts', align: 'center' },
  { id: 'permits', header: 'Permits', align: 'center' },
  { id: 'type', header: 'Type' },
];

// grid: account-info | status | contacts | permits | type
const CUSTOMER_GRID = 'minmax(0, 1fr) 140px 90px 90px 150px';

// ── Customers Page ─────────────────────────────────────────────────────────

function CustomersPage() {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('__all__');
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timerRef.current);
  }, [search]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['customers', debouncedSearch],
    queryFn: () =>
      customersControllerFindAll(
        debouncedSearch ? { search: debouncedSearch } : {},
      ) as unknown as Promise<PaginatedResponse | CustomerListItem[]>,
  });

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['customers', 'stats'],
    queryFn: () =>
      api.get('customers/stats').json<{
        totalCustomers: { value: number; delta: string };
        activeAccounts: { value: number; delta: string };
        pendingVerification: { value: number; delta: string };
        totalPermits: { value: number; delta: string };
      }>(),
  });

  const allCustomers = Array.isArray(data) ? data : (data?.data ?? []);
  const customers =
    statusFilter === '__all__'
      ? allCustomers
      : allCustomers.filter((c) => c.status === statusFilter);
  const total = Array.isArray(data) ? allCustomers.length : (data?.total ?? allCustomers.length);

  return (
    <AppLayout>
      <PageHeader
        title="Customers"
        description="View and manage all customer accounts and their permit activity."
        actions={
          can('customer:create') ? (
            <Button onClick={() => navigate({ to: '/customers/new' })}>
              <Plus size={16} />
              New Customer
            </Button>
          ) : null
        }
      />

      {/* Stats Cards */}
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {statsLoading || !stats ? (
          [...Array(4)].map((_, i) => <StatCardSkeleton key={i} />)
        ) : (
          <>
            <StatCard
              icon={<Building2 size={20} />}
              iconBg="bg-blue-50"
              iconColor="text-blue-500"
              label="Total Customers"
              value={new Intl.NumberFormat('en-US').format(stats.totalCustomers.value)}
              delta={stats.totalCustomers.delta}
            />
            <StatCard
              icon={<UserCheck size={20} />}
              iconBg="bg-emerald-50"
              iconColor="text-emerald-500"
              label="Active Accounts"
              value={new Intl.NumberFormat('en-US').format(stats.activeAccounts.value)}
              delta={stats.activeAccounts.delta}
            />
            <StatCard
              icon={<AlertCircle size={20} />}
              iconBg="bg-amber-50"
              iconColor="text-amber-500"
              label="Pending Verification"
              value={new Intl.NumberFormat('en-US').format(stats.pendingVerification.value)}
              delta={stats.pendingVerification.delta}
            />
            <StatCard
              icon={<FileCheck size={20} />}
              iconBg="bg-cyan-50"
              iconColor="text-cyan-500"
              label="Total Permits"
              value={new Intl.NumberFormat('en-US').format(stats.totalPermits.value)}
              delta={stats.totalPermits.delta}
            />
          </>
        )}
      </div>

      {/* Toolbar */}
      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="relative min-w-56 flex-1">
          <Search
            size={15}
            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, type, or code…"
            className="h-9 w-full rounded-xl border border-gray-200 bg-gray-50 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-brand-100 focus:border-brand-400 transition-all placeholder:text-gray-400"
          />
        </div>

        <FilterPills
          options={CUSTOMER_FILTER_PILLS}
          value={statusFilter}
          onChange={setStatusFilter}
        />

        <button
          type="button"
          className="flex items-center gap-2 rounded-xl border border-gray-200 px-3.5 py-2 text-sm text-gray-500 font-medium hover:bg-gray-50 transition-colors"
        >
          <SlidersHorizontal size={14} />
          Filters
        </button>

        <span className="ml-auto text-xs text-gray-400">
          {total} {total === 1 ? 'account' : 'accounts'}
        </span>
      </div>

      {/* List */}
      <CardDataTable
        gridTemplateColumns={CUSTOMER_GRID}
        avatarWidth="44px"
        columns={CUSTOMER_COLUMNS}
        isLoading={isLoading}
        errorContent={
          isError ? (
            <div className="rounded-2xl border border-error-200 bg-error-50 p-8 text-center">
              <p className="text-sm font-medium text-error-600">Failed to load customers</p>
            </div>
          ) : undefined
        }
        emptyContent={
          <div className="rounded-2xl border border-gray-200 bg-white p-16 text-center shadow-sm">
            <UserRound size={40} className="mx-auto mb-4 text-gray-200" />
            <p className="text-sm font-semibold text-gray-500">No customers found</p>
            <p className="mt-1 text-xs text-gray-400">Try adjusting your search or filters</p>
          </div>
        }
        rows={customers.map(
          (customer): CardDataTableRowDef => ({
            id: customer.id,
            avatar: <GradientAvatar name={customer.name} size="lg" shape="rounded" />,
            cells: {
              account: (
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-gray-900 group-hover:text-brand-600 transition-colors leading-snug">
                    {customer.name}
                  </p>
                  <p className="mt-0.5 text-xs text-gray-500 truncate">{customer.accountType}</p>
                </div>
              ),
              status: <CustomerStatusBadge status={customer.status} />,
              contacts: (
                <div className="text-center">
                  <div className="text-sm font-semibold text-gray-700">
                    {customer._count?.contacts ?? 0}
                  </div>
                  <div className="text-[10px] text-gray-400">contacts</div>
                </div>
              ),
              permits: (
                <div className="text-center">
                  <div className="text-sm font-semibold text-gray-700">
                    {customer._count?.permits ?? 0}
                  </div>
                  <div className="text-[10px] text-gray-400">permits</div>
                </div>
              ),
              type: (
                <span className="rounded-lg bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600">
                  {customer.accountType}
                </span>
              ),
            },
            onClick: () =>
              navigate({ to: '/customers/$customerId', params: { customerId: customer.id } }),
            actions: [
              {
                icon: <Edit2 size={12} />,
                label: 'Edit',
                onClick: (e) => e.stopPropagation(),
              },
              {
                icon: <MoreHorizontal size={13} />,
                label: 'More',
                onClick: (e) => e.stopPropagation(),
              },
            ],
          }),
        )}
      />
    </AppLayout>
  );
}
